import * as analytics from 'analytics/Analytics';
import * as EventConstants from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import {Comment} from 'notebook/ELNConstants';
import ProtocolDAO from 'notebook/protocols/dao/ProtocolDAO';
import notifications from 'notifications';
import EntityStore from 'stores/EntityStore';

function commentHelper(
  protocolID: string,
  {
    linkableID,
    linkType,
    onDescription,
    text,
  }: {
    linkableID: string;
    linkType: string;
    onDescription: boolean;
    text: string;
  }
): JQueryPromise<Comment> {
  AppDispatcher.dispatch({
    actionType: EventConstants.PROTOCOL_COMMENT,
    protocolID,
    linkableID,
    linkType,
    onDescription,
    text,
  });

  return ProtocolDAO.comment(protocolID, {
    text,
    linkableID,
    linkType,
    onDescription,
  })
    .done((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PROTOCOL_COMMENT_COMPLETED,
        protocolID,
        comment: response,
      });
    })
    .fail((response) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PROTOCOL_COMMENT_FAILED,
        protocolID,
        error: response,
      });
    });
}

export default {
  delete(protocolID: string, param: {success?: () => void}) {
    if (param == null) {
      param = {};
    }
    const {success} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTOCOL_DELETE,
      protocolID,
    });
    ProtocolDAO.delete(protocolID)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_DELETE_COMPLETED,
          protocolID,
        });
        if (typeof success === 'function') {
          success();
        }
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_DELETE_FAILED,
          protocolID,
          error: response,
        });
      });
  },

  update(protocolID: string, data: {tagSchemaId: string | null}, param: {success?: () => void}) {
    if (param == null) {
      param = {};
    }
    const {success} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTOCOL_UPDATE,
      protocolID,
      data,
    });

    ProtocolDAO.update(protocolID, data)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_UPDATE_COMPLETED,
          protocolID,
          data,
          serverData: {
            editURL: response?.editURL,
            folder: response?.folder,
          },
        });
        if (typeof success === 'function') {
          success();
        }
      })
      .fail((response) => {
        const friendlyMessage = notifications.getFriendlyError(response);
        if (friendlyMessage != null) {
          notifications.error(friendlyMessage, {id: 'protocol-save-fail', duration: 0});
        }
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_UPDATE_FAILED,
          protocolID,
          error: response,
        });
      });
  },

  open(protocolID: string) {
    analytics.track('protocols', 'open_protocol', {protocol_api_id: protocolID});
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTOCOL_OPEN,
      protocolID,
    });

    ProtocolDAO.get(protocolID)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_OPEN_COMPLETED,
          protocolID,
          protocol: response,
        });
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_OPEN_FAILED,
          protocolID,
          error: response,
        });
      });
  },

  comment(
    protocolID: string,
    options: {
      linkableID: string;
      linkType: string;
      onDescription: boolean;
      text: string;
    }
  ) {
    commentHelper(protocolID, options);
  },

  __dangerouslyCommentWithXHR(
    protocolID: string,
    options: {
      linkableID: string;
      linkType: string;
      onDescription: boolean;
      text: string;
    }
  ) {
    return commentHelper(protocolID, options);
  },

  deleteComment(protocolID: string, commentID: number): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTOCOL_DELETE_COMMENT,
      protocolID,
      commentID,
    });

    ProtocolDAO.deleteComment(protocolID, commentID)
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_DELETE_COMMENT_COMPLETED,
          protocolID,
          commentID,
        });
      })
      .fail((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_DELETE_COMMENT_FAILED,
          protocolID,
          commentID,
          error: response,
        });
      });
  },

  completeStep(protocolID: string, stepID: number | string) {
    const user = EntityStore.currentUser();
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTOCOL_STEP_COMPLETE_COMPLETED,
      protocolID,
      stepID,
    });
    if (user == null) {
      return;
    }
    ProtocolDAO.completeStep(protocolID, stepID).fail(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PROTOCOL_STEP_COMPLETE_FAILED,
        protocolID,
        stepID,
      });
    });
  },

  resetStep(protocolID: string, stepID: number | string) {
    const user = EntityStore.currentUser();
    AppDispatcher.dispatch({
      actionType: EventConstants.PROTOCOL_STEP_RESET_COMPLETED,
      protocolID,
      stepID,
    });
    if (user == null) {
      return;
    }

    ProtocolDAO.resetStep(protocolID, stepID).fail(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PROTOCOL_STEP_RESET_FAILED,
        protocolID,
        stepID,
      });
    });
  },

  resetAllSteps(protocolID: string): void {
    const user = EntityStore.currentUser();
    if (user == null) {
      AppDispatcher.dispatch({
        actionType: EventConstants.PROTOCOL_RESET_STEPS_COMPLETED,
        protocolID,
      });
      return;
    }
    ProtocolDAO.resetAllSteps(protocolID)
      .done(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_RESET_STEPS_COMPLETED,
          protocolID,
        });
      })
      .fail(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.PROTOCOL_RESET_STEPS_FAILED,
          protocolID,
        });
      });
  },
};
